export type HSLColor = {
  h: number
  s: number
  l: number
}

export function hslToHex(h: number, s: number, l: number): string {
  l /= 100
  const a = (s * Math.min(l, 1 - l)) / 100
  const f = (n: number) => {
    const k = (n + h / 30) % 12
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0')
  }
  return `#${f(0)}${f(8)}${f(4)}`.toUpperCase()
}

export function hslToRgb(h: number, s: number, l: number): string {
  l /= 100
  const a = (s * Math.min(l, 1 - l)) / 100
  const f = (n: number) => {
    const k = (n + h / 30) % 12
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
    return Math.round(255 * color)
  }
  return `rgb(${f(0)}, ${f(8)}, ${f(4)})`
}

// Convert hex to HSL
export function hexToHsl(hex: string): HSLColor | null {
  // Remove # if present
  hex = hex.replace(/^#/, '')

  // Handle 3-character hex
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('')
  }

  if (hex.length !== 6) return null

  const r = parseInt(hex.slice(0, 2), 16) / 255
  const g = parseInt(hex.slice(2, 4), 16) / 255
  const b = parseInt(hex.slice(4, 6), 16) / 255

  if (isNaN(r) || isNaN(g) || isNaN(b)) return null

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2

  let h = 0
  let s = 0

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / d + 2) / 6
        break
      case b:
        h = ((r - g) / d + 4) / 6
        break
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  }
}

// Validate hex color
export function isValidHex(hex: string): boolean {
  return /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex)
}

// Normalize hue to 0-360 range
export function normalizeHue(hue: number): number {
  return ((hue % 360) + 360) % 360
}

// Generate dynamic hue range centered around a base hue
export function generateDynamicHues(
  baseHue: number,
  spread: number = 30,
  steps: number = 14,
): number[] {
  const hues: number[] = []
  const stepSize = (spread * 2) / (steps - 1)

  for (let i = 0; i < steps; i++) {
    const hue = normalizeHue(baseHue - spread + i * stepSize)
    hues.push(Math.round(hue))
  }

  return hues
}

// Get hue name for display
export function getHueName(hue: number): string {
  const normalizedHue = normalizeHue(hue)
  if (normalizedHue >= 0 && normalizedHue < 30) return 'Red'
  if (normalizedHue >= 30 && normalizedHue < 60) return 'Orange'
  if (normalizedHue >= 60 && normalizedHue < 90) return 'Yellow'
  if (normalizedHue >= 90 && normalizedHue < 150) return 'Green'
  if (normalizedHue >= 150 && normalizedHue < 210) return 'Cyan'
  if (normalizedHue >= 210 && normalizedHue < 270) return 'Blue'
  if (normalizedHue >= 270 && normalizedHue < 330) return 'Purple'
  return 'Red'
}

export const CURRENT_BRAND_COLOR: HSLColor = { h: 74, s: 99, l: 65 }

// Generation configuration (kept for backwards compatibility)
export const HUES = [
  60, 65, 70, 74, 75, 80, 85, 90, 95, 100, 105, 110, 115, 120,
]
export const SATURATIONS = [100, 95, 90, 85, 80, 75, 70, 65, 60, 55, 50]
// Extended lightness range: from very light (#ffffd5) to very dark (#191b0c)
export const LIGHTNESSES = [
  95, 90, 85, 80, 75, 70, 65, 60, 55, 50, 45, 40, 35, 30, 25, 20, 15, 10, 7,
]

// Full spectrum lightness steps for preview (matching reference: #ffffd5 to #191b0c)
export const PREVIEW_LIGHTNESS_STEPS = [92, 84, 75, 66, 53, 38, 23, 8]
